/**
 * Service worker support — the pure half of explaining why a page has no
 * acequia SW.
 *
 * The acequia SW is a module service worker (`type: 'module'`). Firefox
 * shipped module SWs only in 147: on 146 and older (ESR 140 included) the
 * script is evaluated as a classic script, its first top-level `import` is
 * a syntax error, and register() rejects with nothing more than "threw an
 * exception during script evaluation" (acequia-service-worker-fails-to-
 * register-in-firefox, 2026-10-01). The same opaque error also comes from a
 * module in the SW graph that 404s or is served with a non-JavaScript MIME
 * type, so the message alone can't tell a browser limit from a deployment
 * fault.
 *
 * `moduleServiceWorkerSupport` tells them apart with a feature test, not a
 * UA sniff: WebIDL converts register()'s options dictionary before the
 * algorithm runs, and a browser that knows module SWs reads the `type`
 * member while doing so. Registering a `data:` URL then rejects (the scheme
 * is never allowed) before anything is registered. Verified headless:
 * Firefox 142 and 146 never read `type`; Firefox 157 does; no registration
 * is created in either case.
 */

/**
 * @param {ServiceWorkerContainer} container  navigator.serviceWorker
 * @returns {Promise<boolean|null>} true/false when the probe is conclusive,
 *     null when it could not run (callers must not treat null as "no")
 */
export async function moduleServiceWorkerSupport(container) {
    if (!container || typeof container.register !== 'function') return null
    let typeRead = false
    const options = {
        get type() {
            typeRead = true
            return 'module'
        },
    }
    try {
        // A resolve here would mean a data: script registered — never
        // expected, but if it ever happens don't leave it behind.
        const reg = await container.register('data:text/javascript,', options)
        await reg?.unregister?.()
    } catch {
        // Expected: data: URLs are refused after the options are converted.
    }
    return typeRead
}

/**
 * Classify a register() failure into the status `registerServiceWorker`
 * publishes.
 *
 * @param {unknown} error              what register() rejected with
 * @param {boolean|null} moduleSupport moduleServiceWorkerSupport's answer
 * @returns {{ state: 'unsupported' | 'failed', reason: string, message: string }}
 */
export function classifyRegistrationFailure(error, moduleSupport) {
    const errorText = `${error?.name || 'Error'}: ${error?.message || error}`
    if (moduleSupport === false) {
        return {
            state: 'unsupported',
            reason: 'module-service-worker',
            message:
                'This browser cannot run module service workers (Firefox ' +
                'gained them in 147), so the acequia service worker is not ' +
                'running: no local /groups/ dispatch, mounts or offline ' +
                'cache — requests go over the network. ' +
                `(${errorText})`,
        }
    }
    const evaluation = /script evaluation/i.test(error?.message || '')
    return {
        state: 'failed',
        reason: evaluation ? 'script-evaluation' : 'registration-error',
        message: evaluation
            ? 'The acequia service worker threw while loading. Check that ' +
              'every module it imports is served as JavaScript — ' +
              "import('<sw url>') in a page names the failing module. " +
              `(${errorText})`
            : `Service worker registration failed. (${errorText})`,
    }
}
